import { CalendarPlus, Check, CircleCheck, Plus } from "lucide-react";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Button } from "@/components/button";
import { useGetBrandsByProjectId } from "@/features/brands/shared/hooks/useGetBrandsByProjectId";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId";

import { useSocialPages } from "../../hooks/useSocialPages";
import { BrandAvatar } from "../BrandFilter";

const MAX_STACKED_AVATARS = 5;

const STEPS = [
  {
    description:
      "Pick which brand the post is for. It goes to that brand's Instagram and Facebook.",
    title: "Choose a brand",
  },
  {
    description: "Choose the designs you want to post.",
    title: "Pick your designs",
  },
  {
    description: "Write a caption, preview it, and choose when it goes live.",
    title: "Set the time",
  },
];

export function FirstPostEmptyState({
  onManageAccounts,
  onSchedulePost,
}: {
  onManageAccounts: () => void;
  onSchedulePost: () => void;
}) {
  const projectId = useGetCurrentProjectId();
  const { data: brands = [] } = useGetBrandsByProjectId(projectId);
  const { data } = useSocialPages();
  const pages = data?.pages ?? [];
  const connectedBrands = brands.filter((brand) =>
    pages.some((page) => page.brand.id === brand.id),
  );
  const hiddenBrandsCount = connectedBrands.length - MAX_STACKED_AVATARS;
  const instagramCount = pages.filter(
    (page) => page.platform === "instagram",
  ).length;
  const facebookCount = pages.filter(
    (page) => page.platform === "facebook",
  ).length;

  return (
    <div className="mx-auto flex w-full max-w-[1240px] flex-col gap-xl p-xl">
      <div className="flex items-center gap-md rounded-xl border border-utility-success-200 bg-bg-success-secondary px-xl py-lg">
        <CircleCheck className="size-8 shrink-0 fill-utility-success-700 text-white" />
        <p className="text-sm font-medium text-utility-success-700">
          Meta is connected. You are ready to schedule posts.
        </p>
      </div>

      <section className="flex flex-col items-center gap-3xl rounded-2xl border border-border-secondary bg-bg-primary p-3xl">
        <div className="flex flex-col items-center gap-lg text-center">
          <span className="flex size-16 items-center justify-center rounded-full bg-bg-brand-secondary text-fg-brand-primary">
            <CalendarPlus className="size-6" />
          </span>
          <div className="flex flex-col gap-sm">
            <h2 className="text-2xl font-bold text-text-primary">
              Schedule your first post
            </h2>
            <p className="max-w-[520px] text-sm text-text-tertiary">
              Pick a design, write a caption, and choose a time. It publishes on
              its own.
            </p>
          </div>
          <Button onPress={onSchedulePost} size="lg">
            <AccessibleIcon>
              <Plus />
            </AccessibleIcon>
            Schedule a post
          </Button>
        </div>

        <ol className="grid w-full grid-cols-1 gap-lg md:grid-cols-3">
          {STEPS.map((step, index) => (
            <li
              className="flex flex-col gap-lg rounded-xl bg-bg-secondary p-xl"
              key={step.title}
            >
              <span className="flex size-8 items-center justify-center rounded-full border border-utility-brand-200 bg-bg-primary text-sm font-bold text-text-brand-secondary">
                {index + 1}
              </span>
              <div className="flex flex-col gap-xs">
                <h3 className="font-semibold text-text-primary">
                  {step.title}
                </h3>
                <p className="text-sm text-text-tertiary">{step.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <div className="flex flex-wrap items-center justify-between gap-lg rounded-xl border border-border-secondary bg-bg-primary px-xl py-lg">
        <div className="flex items-center gap-xl">
          <span className="flex -space-x-2">
            {connectedBrands.slice(0, MAX_STACKED_AVATARS).map((brand) => (
              <BrandAvatar
                className="size-10 ring-2 ring-bg-primary"
                key={brand.id}
                name={brand.name}
                profilePicture={brand.profilePicture}
              />
            ))}
            {hiddenBrandsCount > 0 && (
              <span className="flex size-10 items-center justify-center rounded-full bg-bg-secondary text-sm font-semibold text-text-secondary ring-2 ring-bg-primary">
                +{hiddenBrandsCount}
              </span>
            )}
          </span>
          <div className="flex flex-col gap-xs">
            <span className="font-semibold text-text-primary">
              {pluralize(connectedBrands.length, "brand")} connected
            </span>
            <div className="flex flex-wrap gap-x-xl gap-y-xs text-sm font-medium text-text-success-primary">
              <ConnectedCount>
                {pluralize(instagramCount, "Instagram account")}
              </ConnectedCount>
              <ConnectedCount>
                {pluralize(facebookCount, "Facebook page")}
              </ConnectedCount>
            </div>
          </div>
        </div>
        <Button onPress={onManageAccounts} variant="secondary">
          Manage accounts
        </Button>
      </div>
    </div>
  );
}

function ConnectedCount({ children }: { children: string }) {
  return (
    <span className="flex items-center gap-xs">
      <Check className="size-4" strokeWidth={3} />
      {children}
    </span>
  );
}

function pluralize(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}
